// src/version-check.ts
// Detects when a newer build has been deployed while this tab is still
// running the old bundle, and reloads the next time the tab becomes visible
// again — never mid-session, so an active user isn't yanked out from under
// themselves. Mirrors realmweb/src/version-check.ts (this app has no
// Capacitor shell, so there's no native appStateChange listener to add).
// Self-initializing on import — entries just need `import './version-check'`.

const POLL_INTERVAL_MS = 60 * 60 * 1000; // 1 hour — adjust as needed
const BOOT_RETRY_DELAY_MS = 3000;

let bootVersion: string | null = null;
let pendingUpdate = false;
let checking = false;

async function fetchVersion(): Promise<string | null> {
  try {
    const res = await fetch('/version.json?t=' + Date.now());
    if (!res.ok) return null;
    const data = await res.json() as { v: string };
    return String(data.v);
  } catch {
    return null;
  }
}

// A transient failure on the very first fetch shouldn't leave bootVersion
// unset — the next successful check would then adopt whatever's live at
// that point as the baseline, silently swallowing any deploy that happened
// in between. One retry covers a blip without indefinitely delaying boot.
async function fetchBootVersion(): Promise<string | null> {
  const first = await fetchVersion();
  if (first) return first;
  await new Promise((resolve) => setTimeout(resolve, BOOT_RETRY_DELAY_MS));
  return fetchVersion();
}

async function checkVersion(): Promise<void> {
  if (pendingUpdate || checking) return;
  checking = true;
  try {
    const latest = await fetchVersion();
    if (!latest) return;
    if (!bootVersion) { bootVersion = latest; return; }
    if (latest !== bootVersion) pendingUpdate = true;
  } finally {
    checking = false;
  }
}

function reloadIfPending(): void {
  if (pendingUpdate) window.location.reload();
}

async function initVersionCheck(): Promise<void> {
  bootVersion = await fetchBootVersion();

  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'visible') checkVersion().then(reloadIfPending);
  });

  setInterval(checkVersion, POLL_INTERVAL_MS);
}

initVersionCheck();
